import { type ReactNode } from "react";
import { WarningCircle } from "@phosphor-icons/react";

import { SelectionModal } from "./SelectionModal";
import { defaultIconProps } from "../features/shared/ui/icons";

type ConfirmDialogTone = "default" | "primary" | "danger";

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel: string;
  confirmLoadingLabel?: string;
  cancelLabel: string;
  confirmLoading?: boolean;
  confirmTone?: ConfirmDialogTone;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
  icon?: ReactNode;
  variant?: "auto" | "dialog" | "bottom-sheet";
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  confirmLoadingLabel,
  cancelLabel,
  confirmLoading = false,
  confirmTone = "default",
  onConfirm,
  onCancel,
  icon,
  variant = "auto",
}: ConfirmDialogProps) {
  if (!open) {
    return null;
  }

  const handleClose = () => {
    if (confirmLoading) {
      return;
    }
    onCancel();
  };

  const confirmToneClasses: Record<ConfirmDialogTone, string> = {
    default:
      "bg-slate-900 text-white hover:bg-slate-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-400 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200",
    primary:
      "bg-sky-600 text-white hover:bg-sky-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500 dark:bg-sky-500 dark:hover:bg-sky-400",
    danger:
      "bg-red-600 text-white hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-500 dark:bg-red-500 dark:hover:bg-red-400",
  };

  const cancelButtonClasses =
    "w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-400 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800 sm:w-auto";
  const confirmButtonClasses = [
    "w-full rounded-lg px-4 py-2.5 text-sm font-semibold shadow-sm transition disabled:opacity-60 sm:w-auto",
    confirmToneClasses[confirmTone],
  ].join(" ");
  const iconToneWrapperClasses: Record<ConfirmDialogTone, string> = {
    default:
      "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
    primary:
      "bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-200",
    danger:
      "bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-300",
  };
  const iconWrapperClasses = [
    "mt-1 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl",
    iconToneWrapperClasses[confirmTone],
  ].join(" ");

  return (
    <SelectionModal
      title={title}
      closeLabel={cancelLabel}
      onClose={handleClose}
      variant={variant}
    >
      <div className="space-y-6">
        <div className="flex gap-3 sm:gap-4">
          <span className={iconWrapperClasses} aria-hidden>
            {icon ?? <WarningCircle {...defaultIconProps} weight="fill" />}
          </span>
          <div className="space-y-2">
            <p className="text-base font-semibold text-slate-900 dark:text-slate-100">
              {title}
            </p>
            {description ? (
              <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-300">
                {description}
              </p>
            ) : null}
          </div>
        </div>

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            className={cancelButtonClasses}
            onClick={handleClose}
            disabled={confirmLoading}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={confirmButtonClasses}
            onClick={onConfirm}
            disabled={confirmLoading}
          >
            {confirmLoading ? confirmLoadingLabel ?? confirmLabel : confirmLabel}
          </button>
        </div>
      </div>
    </SelectionModal>
  );
}
